/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Button, Grid, Icon, Modal } from '@mairistem/composants';

import { ModalParametresGeneraux } from '@/components/Parametres';
import type { GestionTrimestreActions } from '@/enums';
import ModalGestionTrimestreRowNumerotation from './ModalGestionTrimestreRowNumerotation';
import ModalGestionTrimestreRowGeneration from './ModalGestionTrimestreRowGeneration';
import ModalGestionTrimestreRowEdition from './ModalGestionTrimestreRowEdition';

interface ModalGestionTrimestreProps {
  trimestre: number;
  annee: number;
  numerotation: Date;
  generation: Date;
  edition: Date;
  dateCloture: Date;
  telechargement: boolean;
  loading: boolean;
  onCloture: () => void;
  onNumerotation: (action: GestionTrimestreActions) => void;
  onGeneration: () => void;
  onTelechargement: () => void;
  onEdition: ({
    recenses,
    nonRecenses,
  }: {
    recenses: boolean;
    nonRecenses: boolean;
  }) => void;
  onClose: () => void;
}

const ModalGestionTrimestre = (props: ModalGestionTrimestreProps) => {
  const {
    trimestre,
    annee,
    numerotation,
    generation,
    edition,
    dateCloture,
    telechargement,
    loading,
    onCloture,
    onNumerotation,
    onGeneration,
    onTelechargement,
    onEdition,
    onClose,
  } = props;

  const [openModalParametres, setOpenModalParametres] =
    React.useState<boolean>(false);

  const renderSeparator = React.useCallback(
    (displayParametre = false) => (
      <Grid.Row style={{ padding: 0 }}>
        <Grid.Column
          width={8}
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            height: '4em',
            padding: 0,
          }}
        >
          <div
            style={{
              width: '4em',
              rotate: '90deg',
              border: 'none',
              borderTop: '3px dashed black',
            }}
          />
        </Grid.Column>
        <Grid.Column
          width={8}
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
          }}
        >
          {displayParametre && (
            // biome-ignore lint/a11y/useKeyWithClickEvents: need
            <p
              onClick={() => setOpenModalParametres(true)}
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              data-testid="parametres-trimestre-bouton"
            >
              Paramètres
            </p>
          )}
        </Grid.Column>
      </Grid.Row>
    ),
    [],
  );

  return (
    <>
      <Modal closeIcon open onClose={onClose}>
        <Modal.Header
          content={`Gestion du ${trimestre === 1 ? `${trimestre}er` : `${trimestre}ème`} trimestre ${annee}`}
        />
        <Modal.Content>
          <Grid style={{ padding: '2em' }}>
            <ModalGestionTrimestreRowNumerotation
              numerotation={numerotation}
              loading={loading}
              onNumerotation={onNumerotation}
              isClotured={!!dateCloture}
            />
            {renderSeparator(true)}
            <ModalGestionTrimestreRowGeneration
              generation={generation}
              numerotation={numerotation}
              loading={loading}
              onGeneration={onGeneration}
              onTelechargement={onTelechargement}
              isClotured={!!dateCloture}
            />
            {renderSeparator()}
            <ModalGestionTrimestreRowEdition
              generation={generation}
              edition={edition}
              loading={loading}
              onEdition={onEdition}
              isClotured={!!dateCloture}
            />
          </Grid>
        </Modal.Content>
        <Modal.Actions>
          <Button
            basic
            size="small"
            icon="close"
            content="Fermer"
            onClick={onClose}
          />
          <Button
            positive
            onClick={onCloture}
            disabled={!telechargement || loading}
          >
            <Icon.Group>
              <Icon name="folder outline" />
              {!dateCloture ? (
                <Icon
                  name="checkmark"
                  corner
                  style={{ fontSize: '0.7em', top: 7, right: -1 }}
                />
              ) : (
                <Icon
                  name="x"
                  corner
                  style={{ fontSize: '0.7em', top: 7, right: -1 }}
                />
              )}
            </Icon.Group>
            {dateCloture ? 'Déclôturer' : 'Clôturer'}
          </Button>
        </Modal.Actions>
      </Modal>

      {openModalParametres && (
        <ModalParametresGeneraux
          onClose={() => setOpenModalParametres(false)}
          redirect={false}
          gestionTrimestre
          data-testid="modal-parametres-gestion-trimestre"
        />
      )}
    </>
  );
};

export default ModalGestionTrimestre;
